/* eslint-disable no-useless-escape */
import * as React from 'react';
import {
    Modal,
    Button,
    Grid,
    XFilledIcon
} from 'react-unity';
import './RuleModal.css';
import TextAreaField from '../../../common/form-controls/TextareaField';
import TextInputField from '../../../common/form-controls/TextInputField';
import { AbstractRulesForm, IRuleStateForm, RulesFormProps } from './AbstractRulesForm';
import { RuleRegion } from '../../../../models/enums/DNDR/RuleRegion';
import SelectField from '../../../common/form-controls/SelectField';
import MultipleSelectField from '../../../common/form-controls/MultipleSelectField';
import { RuleOperation } from '../../../../models/enums/DNDR/RuleOperation';
import { DoNotDecryptRequestsDirection } from '../../../../models/enums/DNDR/DoNotDecryptRequestsDirection';
import DoNotDecryptRule from '../../../../models/entities/DoNotDecryptRule';
import { Address4 } from 'ip-address';

interface RuleModalSubToSubAddNewProps extends RulesFormProps {
    subscriptionRequestName: string;
    onConfirmMultiple?: (rules: DoNotDecryptRule[]) => void;
}

interface SubToSubAddNewRuleStateForm extends IRuleStateForm {
    name: string;
    destinationURLs: string;
    // Arrays to hold multi-select values
    sourceIPsArray: string[];
    destinationIPsArray: string[];
    destinationPortsArray: string[];
    destinationURLsArray: string[];
}

interface RuleModalSubToSubAddNewState {
    stateForm: SubToSubAddNewRuleStateForm;
}

export default class RuleModalSubToSubAddNew extends AbstractRulesForm<
    RuleModalSubToSubAddNewProps,
    RuleModalSubToSubAddNewState
> {
    constructor(props: RuleModalSubToSubAddNewProps) {
        super(props);
        this.state = {
            stateForm: this.getDefaultForm() as SubToSubAddNewRuleStateForm,
        };
        this.initFormModel();
    }

    isURLValid = (url) => /^(((http(s)?:\/\/.)?(www\.)?)|(\*\.)?)[a-zA-Z0-9]+[-a-zA-Z0-9@:%._\+~#=]{1,256}\.[a-z]{2,6}\b([-a-zA-Z0-9@:%_\+.~#?&//=]*)/.test(url);

    initFormModel() {
        super.initFormModel();

        this.formModel.addField('name', {
            getValue: () => this.state.stateForm.name,
            validation: {
                required: true,
                rules: [
                    {
                        assert: () => this.state.stateForm.operation !== RuleOperation.Add
                            || this.state.stateForm.name?.length <= 24,
                        message: 'Name cannot be longer than 24 characters.',
                    },
                    {
                        assert: () => !this.state.stateForm.name?.includes(' '),
                        message: 'Name cannot contain spaces.',
                    },
                    {
                        assert: () => this.state.stateForm.operation !== RuleOperation.Add
                            || /^[a-zA-Z](?:[a-zA-Z_-]*[a-zA-Z])?$/.test(this.state.stateForm.name ?? ''),
                        message: 'Only letters (uppercase and lowercase), dashes, underscores are allowed. No trailing or beginning dashes or underscores.',
                    }
                ]
            },
        });

        this.formModel.addField('destinationURLs', {
            getValue: () => this.state.stateForm.destinationURLs,
            validation: { required: false, rules: [] }
        });

        // Values come from multi-select dropdowns; no text-input format validation needed
        this.formModel.fields.sourceIPs.validation.required = () => true;
        this.formModel.fields.sourceIPs.validation.rules = [];
        this.formModel.fields.destinationIPs.validation.required = () => false;
        this.formModel.fields.destinationIPs.validation.rules = [];
        this.formModel.fields.destinationPorts.validation.required = () => true;
        this.formModel.fields.destinationPorts.validation.rules = [];
    }

    getDefaultFormState() {
        return {
            operation: RuleOperation.Add,
            name: '',
            destinationURLs: '',
            sourceIPsArray: [] as string[],
            destinationIPsArray: [] as string[],
            destinationPortsArray: [] as string[],
            destinationURLsArray: [] as string[],
        };
    }

    loadIPsValidations(_direction) {}

    getRegionOptions(): RuleRegion[] {
        const SubToSubRegions = new RuleRegion().listByDirection(
            DoNotDecryptRequestsDirection.SubscriptionToSubscription,
            this.props.validVirtualNetworks
        );
        const wwSubscriptionsNames = ['APPS_SharedSvcs_WW_DEV', 'APPS_SharedSvcs_WW_LAB', 'APPS_SharedSvcs_WW_PRD'];
        return wwSubscriptionsNames.includes(this.props.subscriptionRequestName)
            ? SubToSubRegions
            : SubToSubRegions.filter(region => region !== RuleRegion.WW);
    }

    getFilteredMappings() {
        if (!this.props.completedPORRules?.hasCompletedPORRules || !this.props.completedPORRules?.ruleMappings) {
            return [];
        }
        // SubToSub POR rules have Sub trafficDirection
        return this.props.completedPORRules.ruleMappings.filter(m => m.trafficDirection == 'Sub');
    }

    // ─── Sort helpers ─────────────────────────────────────────────────────────

    sortIPOptions(options: { value: string; text: string }[]): { value: string; text: string }[] {
        return options.sort((a, b) => {
            try {
                const ipA = new Address4(a.value.split('/')[0]);
                const ipB = new Address4(b.value.split('/')[0]);
                const numA = ipA.toArray();
                const numB = ipB.toArray();
                for (let i = 0; i < 4; i++) {
                    if (numA[i] !== numB[i]) return numA[i] - numB[i];
                }
                // When network addresses are equal, sort by prefix length ascending
                const prefixA = a.value.includes('/') ? parseInt(a.value.split('/')[1]) : 32;
                const prefixB = b.value.includes('/') ? parseInt(b.value.split('/')[1]) : 32;
                return prefixA - prefixB;
            } catch {
                return a.value.localeCompare(b.value);
            }
        });
    }

    sortIPArray(ips: string[]): string[] {
        return [...ips].sort((a, b) => {
            try {
                const ipA = new Address4(a.split('/')[0]);
                const ipB = new Address4(b.split('/')[0]);
                const numA = ipA.toArray();
                const numB = ipB.toArray();
                for (let i = 0; i < 4; i++) {
                    if (numA[i] !== numB[i]) return numA[i] - numB[i];
                }
                const prefixA = a.includes('/') ? parseInt(a.split('/')[1]) : 32;
                const prefixB = b.includes('/') ? parseInt(b.split('/')[1]) : 32;
                return prefixA - prefixB;
            } catch {
                return a.localeCompare(b);
            }
        });
    }

    sortPortArray(ports: string[]): string[] {
        return [...ports].sort((a, b) => Number(a) - Number(b));
    }

    sortPortOptions(options: { value: string; text: string }[]): { value: string; text: string }[] {
        return [...options].sort((a, b) => Number(a.value) - Number(b.value));
    }

    // ─── Option list builders ─────────────────────────────────────────────────

    getSourceIPOptions() {
        const mappings = this.getFilteredMappings();
        const ips = [...new Set(mappings.map(m => m.sourceIP))];
        return this.sortIPOptions(ips.map(ip => ({ value: ip, text: ip })));
    }

    getDestinationPortOptions() {
        const mappings = this.getFilteredMappings();
        const selectedSrcIPs = this.state.stateForm.sourceIPsArray ?? [];
        if (selectedSrcIPs.length === 0) return [];
        const ports = [...new Set(
            mappings
                .filter(m => selectedSrcIPs.includes(m.sourceIP))
                .map(m => m.destinationPort)
        )];
        return this.sortPortOptions(ports.map(p => ({ value: p, text: p })));
    }

    getDestinationIPOptions() {
        const mappings = this.getFilteredMappings();
        const selectedSrcIPs = this.state.stateForm.sourceIPsArray ?? [];
        const selectedPorts = this.state.stateForm.destinationPortsArray ?? [];
        if (selectedSrcIPs.length === 0 || selectedPorts.length === 0) return [];
        const ips = [...new Set(
            mappings
                .filter(m => selectedSrcIPs.includes(m.sourceIP) && selectedPorts.includes(m.destinationPort))
                .map(m => m.destinationIP)
                .filter(ip => ip != null)
        )];
        return this.sortIPOptions(ips.map(ip => ({ value: ip, text: ip })));
    }

    getDestinationURLOptions() {
        const mappings = this.getFilteredMappings();
        const selectedSrcIPs = this.state.stateForm.sourceIPsArray ?? [];
        const selectedPorts = this.state.stateForm.destinationPortsArray ?? [];
        if (selectedSrcIPs.length === 0 || selectedPorts.length === 0) return [];
        const urls = [...new Set(
            mappings
                .filter(m => selectedSrcIPs.includes(m.sourceIP) && selectedPorts.includes(m.destinationPort))
                .map(m => m.destinationURL)
                .filter(url => url != null)
        )];
        // URLs are sorted alphabetically
        return [...urls].sort().map(url => ({ value: url, text: url }));
    }

    // ─── Multi-select change handlers ─────────────────────────────────────────

    handleSourceIPsChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value;
        const current = this.state.stateForm.sourceIPsArray ?? [];
        const updated = current.includes(value) ? current.filter(v => v !== value) : [...current, value];
        const form = { ...this.state.stateForm };
        form.sourceIPsArray = updated;
        form.sourceIPs = updated.join(';');
        // Changing Source IPs clears all downstream selections
        form.destinationPortsArray = [];
        form.destinationPorts = '';
        form.destinationIPsArray = [];
        form.destinationIPs = '';
        form.destinationURLsArray = [];
        form.destinationURLs = '';
        this.setState({ stateForm: form });
    };

    handleDestinationPortsChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value;
        const current = this.state.stateForm.destinationPortsArray ?? [];
        const updated = current.includes(value) ? current.filter(v => v !== value) : [...current, value];
        const form = { ...this.state.stateForm };
        form.destinationPortsArray = updated;
        form.destinationPorts = updated.join(';');
        // Changing Destination Ports clears destination IP and URL selections
        form.destinationIPsArray = [];
        form.destinationIPs = '';
        form.destinationURLsArray = [];
        form.destinationURLs = '';
        this.setState({ stateForm: form });
    };

    handleDestinationIPsChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value;
        const current = this.state.stateForm.destinationIPsArray ?? [];
        const updated = current.includes(value) ? current.filter(v => v !== value) : [...current, value];
        const form = { ...this.state.stateForm };
        form.destinationIPsArray = updated;
        form.destinationIPs = updated.join(';');
        // Destination IP and URL are mutually exclusive
        form.destinationURLsArray = [];
        form.destinationURLs = '';
        this.setState({ stateForm: form });
    };

    handleDestinationURLsChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value;
        const current = this.state.stateForm.destinationURLsArray ?? [];
        const updated = current.includes(value) ? current.filter(v => v !== value) : [...current, value];
        const form = { ...this.state.stateForm };
        form.destinationURLsArray = updated;
        form.destinationURLs = updated.join(';');
        // Destination URL and IP are mutually exclusive
        form.destinationIPsArray = [];
        form.destinationIPs = '';
        this.setState({ stateForm: form });
    };

    // ─── Submit: generate rules in order Source IPs → Destination Ports → Destination URLs → Destination IPs ───

    handleRequest = async (e) => {
        this.props.onClose(e);

        const mappings = this.getFilteredMappings();

        const selectedSrcIPs = this.sortIPArray(this.state.stateForm.sourceIPsArray ?? []);
        const selectedPorts  = this.sortPortArray(this.state.stateForm.destinationPortsArray ?? []);
        const selectedURLs   = [...(this.state.stateForm.destinationURLsArray ?? [])].sort();
        const selectedDstIPs = this.sortIPArray(this.state.stateForm.destinationIPsArray ?? []);

        const rules: DoNotDecryptRule[] = [];

        // URL branch: Destination IP and URL are mutually exclusive; URLs take priority when selected
        if (selectedURLs.length > 0) {
            for (const srcIP of selectedSrcIPs) {
                for (const port of selectedPorts) {
                    for (const url of selectedURLs) {
                        const exists = mappings.some(
                            m => m.sourceIP === srcIP && m.destinationPort === port && m.destinationURL === url
                        );
                        if (exists) {
                            const model = this.formModel.create(DoNotDecryptRule);
                            model.sourceIPs = srcIP;
                            model.destinationPorts = port;
                            model.destinationURLs = url;
                            model.destinationIPs = '';
                            rules.push(model);
                        }
                    }
                }
            }
        } else {
            // IP branch
            for (const srcIP of selectedSrcIPs) {
                for (const port of selectedPorts) {
                    for (const dstIP of selectedDstIPs) {
                        const exists = mappings.some(
                            m => m.sourceIP === srcIP && m.destinationPort === port && m.destinationIP === dstIP
                        );
                        if (exists) {
                            const model = this.formModel.create(DoNotDecryptRule);
                            model.sourceIPs = srcIP;
                            model.destinationPorts = port;
                            model.destinationIPs = dstIP;
                            model.destinationURLs = '';
                            rules.push(model);
                        }
                    }
                }
            }
        }

        if (this.props.onConfirmMultiple) {
            this.props.onConfirmMultiple(rules);
        } else {
            rules.forEach(r => this.props.onConfirm(r));
        }

        this.resetState();
    };

    // ─── Render ───────────────────────────────────────────────────────────────

    render() {
        const canEdit = this.formCanBeEdited();

        const srcOptions    = this.getSourceIPOptions();
        const portOptions   = this.getDestinationPortOptions();
        const dstIPOptions  = this.getDestinationIPOptions();
        const dstURLOptions = this.getDestinationURLOptions();

        const srcChecked    = this.state.stateForm.sourceIPsArray ?? [];
        const portChecked   = this.state.stateForm.destinationPortsArray ?? [];
        const dstIPChecked  = this.state.stateForm.destinationIPsArray ?? [];
        const dstURLChecked = this.state.stateForm.destinationURLsArray ?? [];

        const srcLabel    = srcChecked.length > 0    ? srcChecked.join(', ')    : 'Select Source IP(s)';
        const portLabel   = portChecked.length > 0   ? portChecked.join(', ')   : 'Select Destination Port(s)';
        const dstIPLabel  = dstIPChecked.length > 0  ? dstIPChecked.join(', ')  : 'Select Destination IP(s)';
        //const dstURLLabel = dstURLChecked.length > 0 ? dstURLChecked.join(', ') : 'Select Destination URL(s)';

        const hasDestination = dstIPChecked.length > 0 || dstURLChecked.length > 0;
        const isFormValid =
            this.state.stateForm.name?.length > 0 &&
            this.state.stateForm.region != null &&
            srcChecked.length > 0 &&
            portChecked.length > 0 &&
            hasDestination &&
            (this.state.stateForm.description?.trim().length ?? 0) >= 5;

        // Destination IP and URL are mutually exclusive
        const dstIPDisabled  = !canEdit || portChecked.length === 0 || dstURLChecked.length > 0;
        //const dstURLDisabled = !canEdit || portChecked.length === 0 || dstIPChecked.length > 0;

        return (
            <Modal show={this.props.visible} onHide={() => this.props.onClose}>
                <Modal.Window className="ruleModal">
                    <Modal.Window.Header>
                        <Modal.Window.Header.Title>Add New Rule (Auto-match completed PORs)</Modal.Window.Header.Title>
                        <Modal.Window.Header.CloseButton onClick={this.props.onClose}>
                            <XFilledIcon size='small' />
                        </Modal.Window.Header.CloseButton>
                    </Modal.Window.Header>
                    <Modal.Window.Body>
                        <Grid variant="2-up">
                            <Grid.Item>
                                <TextInputField
                                    label="Rule Name"
                                    value={this.state.stateForm.name}
                                    validation={this.formModel.fields.name.validation}
                                    onChange={(event) => this.handleStateFormChange('name', event.target.value)}
                                    disabled={!canEdit}
                                />
                            </Grid.Item>
                            <Grid.Item>
                                <SelectField
                                    {...this.stateFormHandler().region}
                                    options={this.getRegionOptions()}
                                    disabled={!canEdit}
                                />
                            </Grid.Item>
                        </Grid>

                        {/* Row 1: Source (IP) + Destination Port */}
                        <Grid variant="2-up">
                            <Grid.Item>
                                {canEdit ? (
                                    <MultipleSelectField
                                        label="Source (IP)"
                                        value={srcLabel}
                                        options={srcOptions.map(o => ({ ...o, checked: srcChecked.includes(o.value) }))}
                                        onChange={this.handleSourceIPsChange}
                                        validation={this.formModel.fields.sourceIPs.validation}
                                        note="Select one or more source IPs."
                                        disabled={!canEdit}
                                    />
                                ) : (
                                    <TextAreaField label="Source (IP)" value={srcChecked.join('\n')} disabled={true} />
                                )}
                            </Grid.Item>
                            <Grid.Item>
                                {canEdit ? (
                                    <MultipleSelectField
                                        label="Destination Port"
                                        value={portLabel}
                                        options={portOptions.map(o => ({ ...o, checked: portChecked.includes(o.value) }))}
                                        onChange={this.handleDestinationPortsChange}
                                        validation={this.formModel.fields.destinationPorts.validation}
                                        note="Select one or more destination ports."
                                        disabled={!canEdit || srcChecked.length === 0 || portOptions.length === 0}
                                    />
                                ) : (
                                    <TextAreaField label="Destination Port" value={portChecked.join('\n')} disabled={true} />
                                )}
                            </Grid.Item>
                        </Grid>

                        {/* Row 2: Destination IP + Destination URLs (shown after port is selected) */}
                        {portChecked.length > 0 && (
                            <Grid variant="2-up">
                                <Grid.Item>
                                    {canEdit ? (
                                        <MultipleSelectField
                                            label="Destination IP"
                                            value={dstIPLabel}
                                            options={dstIPOptions.map(o => ({ ...o, checked: dstIPChecked.includes(o.value) }))}
                                            onChange={this.handleDestinationIPsChange}
                                            validation={this.formModel.fields.destinationIPs.validation}
                                            note={dstURLChecked.length > 0 ? 'Clear Destination URLs first.' : 'Select one or more destination IPs.'}
                                            disabled={dstIPDisabled}
                                        />
                                    ) : (
                                        <TextAreaField label="Destination IP" value={dstIPChecked.join('\n')} disabled={true} />
                                    )}
                                </Grid.Item>                                
                            </Grid>
                        )}

                        <Grid variant="2-up">
                            <Grid.Item>
                                <TextAreaField
                                    {...this.stateFormHandler().description}
                                    label="Description"
                                    disabled={!canEdit}
                                />
                            </Grid.Item>
                        </Grid>
                    </Modal.Window.Body>
                    {!this.props.isReadOnly &&
                        <Modal.Window.Footer>
                            <Grid variant="2-up">
                                <Grid.Item>
                                    <Button
                                        variant="primary"
                                        disabled={!isFormValid}
                                        onClick={this.handleRequest}
                                    >
                                        Create
                                    </Button>
                                </Grid.Item>
                                <Grid.Item>
                                    <Button variant="secondary" onClick={this.handleCancel}>
                                        Cancel
                                    </Button>
                                </Grid.Item>
                            </Grid>
                        </Modal.Window.Footer>
                    }
                </Modal.Window>
            </Modal>
        );
    }
}